import React, { ReactElement } from 'react';
import isEqual from 'lodash.isequal';
import { Badge } from 'b2b-components/theme/components/Badge';
import { Tabs } from 'b2b-components/theme/components/Tabs';
import { ProductAccessories } from 'b2b-apps/core/product/components/ProductDetails/ProductAccessories';
import { isEmpty } from 'b2b-apps/shop/common/utils/empty/empty';
import { useTranslate } from 'b2b-apps/shop/i18n/hooks';
import {
  ProductDescription,
} from 'b2b-apps/shop/product/components/ProductDetails/ProductDescription/ProductDescription';
import { ProductDocuments } from 'b2b-apps/shop/product/components/ProductDetails/ProductDocuments';
import { ProductSpecification } from 'b2b-apps/shop/product/components/ProductDetails/ProductSpecification';
import { ProductVariantsMatrix } from 'b2b-apps/shop/product/components/ProductDetails/ProductVariantsMatrix';
import { useProductAccessoriesEffect, useProductVariantsMatrixEffect } from 'b2b-apps/shop/product/hooks';
import { ProductAccessoryCategoryType } from 'b2b-apps/shop/product/redux/productAccessories/types';
import { EnhancedComponentProps, EnhancedComponentType, InjectedProps, WrappedComponentType } from './';

const withContainer = (Component: WrappedComponentType): EnhancedComponentType => {
  const WithContainer = (props: EnhancedComponentProps): ReactElement => {
    const t = useTranslate();
    const { description, sku } = props;
    const accessories = useProductAccessoriesEffect(sku);
    const variantsMatrix = useProductVariantsMatrixEffect(sku);
    const columnLeft = [];
    const columnRight = [];

    if (
      description
      && (description.subtitle !== ''
        || description.lead !== ''
        || (Array.isArray(description.dangerDetails)
          && description.dangerDetails.length > 0)
        || (Array.isArray(description.dangerIcons)
          && description.dangerIcons.length > 0)
        || (Array.isArray(description.paragraphs)
          && description.paragraphs.length > 0)
        || (Array.isArray(description.banners)
          && description.banners.length > 0)
        || (Array.isArray(description.remarks) && description.remarks.length > 0))
    ) {
      columnLeft.push(
        <Tabs.Tab title={t('Description')} key="description">
          <ProductDescription description={description} />
        </Tabs.Tab>,
      );
    }

    //accessories are always in the right column
    if (
      accessories.isLoaded
      && !isEmpty(accessories.data)
      && accessories.data
    ) {
      const accessoriesCount = accessories.data.reduce(
        (sum, category: ProductAccessoryCategoryType) => sum + category.count,
        0,
      );
      const label = (
        <>
          <span>{t('Accessories')}</span>
          <Badge>{accessoriesCount}</Badge>
        </>
      );

      columnRight.push(
        <Tabs.Tab title={label} key="accessories">
          <ProductAccessories sku={sku} />
        </Tabs.Tab>,
      );
    }

    //Specifications may be in any of columns
    if (description.specification) {
      const component = (
        <Tabs.Tab title={t('Specification')} key="specification">
          <ProductSpecification specification={description.specification} />
        </Tabs.Tab>
      );

      if (columnRight.length) {
        columnLeft.push(component);
      } else {
        columnRight.push(component);
      }
    }

    //documents are always in the left column
    if (
      description
      && (description.documents.regular.length > 0
        || description.documents.danger.length > 0)
    ) {
      const label = (
        <>
          <span>{t('Documents')}</span>
          <Badge>
            {description.documents.danger.length
              + description.documents.regular.length}
          </Badge>
        </>
      );

      columnLeft.push(
        <Tabs.Tab title={label} key="documents">
          {description.documents.regular.length > 0 && (
            <ProductDocuments documents={description.documents.regular} />
          )}
          {description.documents.danger.length > 0 && (
            <ProductDocuments
              documents={description.documents.danger}
              label={t('Danger documents')}
            />
          )}
        </Tabs.Tab>,
      );
    }

    //variants matrix are always in the right column
    if (
      variantsMatrix.isLoaded
      && !isEmpty(variantsMatrix.data)
      && variantsMatrix.data
    ) {
      columnRight.push(
        <Tabs.Tab title={t('Variants')} key="variants">
          <ProductVariantsMatrix variantsMatrix={variantsMatrix.data} />
        </Tabs.Tab>,
      );
    }

    const injectedProps: InjectedProps = {
      columnLeft,
      columnRight,
    };

    return <Component {...props} {...injectedProps} />;
  };

  return React.memo(WithContainer, (prevProps, nextProps) => isEqual(prevProps, nextProps));
};

export { withContainer };
